import { Phone, ArrowRight, Award, BookOpen, Heart, GraduationCap } from 'lucide-react';

const DOCTOR_HERO = "https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/4eg24kjf_IMG_2610.JPG";
const DOCTOR_ABOUT = "https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/yn5kof0y_IMG_3786.JPG";
const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica.";

export const SobrePage = () => {
  return (
    <main data-testid="sobre-page" className="pt-24">
      {/* Hero */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#B8944D] mb-6">
                Sobre o Dr. Eduardo Hadas
              </p>
              
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Antes de ser médico,<br />
                <span className="text-[#B8944D]">eu fui paciente</span>.
              </h1>
              
              <p className="text-lg text-[#666666] leading-relaxed mb-6">
                Minha história pessoal com obesidade, erro terapêutico e reposição hormonal 
                é o que define a forma como eu pratico medicina hoje.
              </p>
              
              <div className="flex flex-wrap gap-4 text-sm text-[#666666]">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#B8944D]" />
                  <span>CRM-PR 40929</span>
                </div>
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-[#B8944D]" />
                  <span>Universidade Positivo</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#B8944D]" />
                  <span>Pós-graduação PUC-PR</span>
                </div>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-md overflow-hidden">
                <img 
                  src={DOCTOR_ABOUT}
                  alt="Dr. Eduardo Hadas"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quem eu sou */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Quem eu sou
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Meu nome é Dr. Eduardo Hadas, médico formado pela Universidade Positivo em 2018, 
                com pós-graduação em Urgências e Emergências Médicas pela Pontifícia Universidade 
                Católica do Paraná e aperfeiçoamento em emagrecimento e terapias endovenosas pela Academia G.A.
              </p>
              
              <p>
                Mas antes de ser médico — e talvez exatamente por isso — eu fui paciente.
              </p>
              
              <p>
                Desde a infância convivi com a obesidade e com o efeito sanfona. Fui a &quot;criança gordinha&quot;, 
                emagreci na adolescência e voltei a ganhar peso na vida adulta. Entrei na faculdade com 
                cerca de 70 kg e me formei com quase 90 kg.
              </p>
              
              <p>
                Mesmo com acompanhamento médico em alguns momentos, faltava algo essencial: um plano 
                realmente direcionado, contínuo e individualizado para emagrecimento e equilíbrio hormonal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* O erro que mudou minha vida */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O erro que mudou minha vida
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Após a graduação, iniciei tratamentos com medicações para emagrecimento e terapias 
                hormonais prescritas por médicos.
              </p>
              
              <p>
                O que poucos falam — e eu vivi na pele — é que, quando isso não é feito com critério 
                e acompanhamento adequado, o eixo hormonal pode ser prejudicado.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                No meu caso, houve inibição definitiva da produção hormonal, levando à necessidade 
                de reposição contínua.
              </p>
              
              <p>
                Foi nesse ponto que algo mudou.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quando eu decidi mergulhar nos estudos */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="aspect-square rounded-md overflow-hidden">
                <img 
                  src={DOCTOR_HERO}
                  alt="Dr. Eduardo Hadas em seu consultório"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div>
              <h2 
                className="section-title mb-8"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Quando eu decidi mergulhar nos estudos
              </h2>
              
              <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
                <p>
                  Ao invés de apenas aceitar a situação, eu mergulhei nos estudos.
                </p>
                
                <p>
                  Passei a aprofundar meus conhecimentos em reposição hormonal, emagrecimento clínico, 
                  terapias nutricionais e tratamentos endovenosos.
                </p>
                
                <p>
                  Estudei porque precisava — e apliquei em mim mesmo, com responsabilidade médica.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O resultado que transformou tudo */}
      <section className="section-padding bg-[#B68A5A]/10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-8 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O resultado que transformou tudo
            </h2>
            
            {/* Antes e Depois */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="relative">
                <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/85bop39g_ANTES.JPG"
                    alt="Dr. Eduardo Hadas - Antes"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 bg-[#2A2A2A]/80 text-white px-4 py-2 rounded-md text-sm font-medium">
                  Antes
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/i3c0vubh_DEPOIS.JPG"
                    alt="Dr. Eduardo Hadas - Depois"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] text-white px-4 py-2 rounded-md text-sm font-medium">
                  Depois
                </div>
              </div>
            </div>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed text-center">
              <p>
                O resultado foi transformador: saí de <span className="text-[#B8944D] font-medium">117 kg para 85 kg</span>, 
                mantendo a reposição hormonal ajustada e associada a um tratamento de emagrecimento bem conduzido.
              </p>
              
              <p>
                Não foi apenas o peso que mudou.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Mudou a disposição, a autoestima, a relação com o corpo, com o trabalho e com a vida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* A pergunta que definiu meu caminho */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              A pergunta que definiu meu caminho
            </h2>
            
            <blockquote className="text-2xl md:text-3xl text-[#2A2A2A] leading-relaxed mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              &quot;Se isso funcionou comigo, por que não levar esse cuidado para outras pessoas?&quot;
            </blockquote>
            
            <div className="w-16 h-px bg-[#B8944D]" />
          </div>
        </div>
      </section>

      {/* Por que eu abri minha clínica */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Por que eu abri minha clínica
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Em 2025, abri minha clínica com um propósito muito claro: oferecer um acompanhamento real, 
                próximo e humano em emagrecimento, reposição hormonal e terapias nutricionais endovenosas.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Aqui, o paciente não recebe apenas uma prescrição.
              </p>
              
              <p>
                Recebe presença médica, orientação contínua e ajustes sempre que necessário.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meu modelo de acompanhamento */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Meu modelo de acompanhamento
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p className="text-[#2A2A2A] font-medium">
                Todos os meus pacientes em acompanhamento têm acesso direto a mim.
              </p>
              
              <p>
                Porque emagrecer, equilibrar hormônios e recuperar saúde não é um evento pontual — é um processo.
              </p>
              
              <p>
                E processos exigem suporte, escuta e ajustes ao longo do caminho.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Por que eu faço o que faço */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Por que eu faço o que faço
            </h2>
            
            <div className="space-y-4 text-xl text-[#2A2A2A] leading-relaxed" style={{ fontFamily: 'var(--font-heading)' }}>
              <p>Eu sei o que é tentar e não conseguir.</p>
              <p>Eu sei o que é errar no tratamento.</p>
              <p>Eu sei o que é se sentir preso ao próprio corpo.</p>
              <p className="text-[#B8944D]">E é justamente por isso que faço o que faço hoje.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meu objetivo com você */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Meu objetivo com você
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Meu objetivo é ajudar você a conquistar não apenas um corpo diferente, mas uma vida 
                com mais saúde, bem-estar físico e equilíbrio emocional.
              </p>
              
              <p>
                Se você busca um tratamento sério, baseado em conhecimento médico e experiência real, 
                será um prazer caminhar com você nessa jornada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer um acompanhamento médico sério,{' '}
              <span className="text-[#B8944D]">vamos conversar</span>.
            </h2>
            
            <p className="text-lg text-white/70 mb-10">
              Agende uma avaliação médica e entenda se o meu acompanhamento faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sobre-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
